import Information16 from '@carbon/icons-react/es/information/16';
import { FormInstance, Tooltip } from 'antd';
import { Dictionary } from 'lodash';
import moment from 'moment';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Account } from 'api/account';
import { Role } from 'api/models';
import { RateCardLite, RateCardVersion, RoleRate } from 'api/rate-cards';
import { DatePicker } from 'components/atoms/date-picker';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import { Option } from 'components/atoms/select';
import { MultiSelectPopover } from 'components/atoms/select/popover';
import { useTranslation } from 'hooks/use-translation';
import { getTimezoneLabelWithOffset } from 'utils/timezone';
import { RateCardCreationMeta, UpdateRateForCurrency } from '../types';
import { RatesTable } from './rates-table';

const InputWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: var(--spacing-05);
  ${Form.Item} {
    width: 100%;
    ${DatePicker} {
      width: 100%;
    }
  }
`;

const StyledFormLabel = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;

interface Props {
  form: FormInstance<RateCardCreationMeta>;
  account: Account;
  roleRates: Dictionary<RoleRate>;
  selectedRateCard: RateCardLite | undefined;
  selectedRateCardVersion: RateCardVersion;
  rateCardVersions: RateCardVersion[];
  isCurrentRateCardVersionReadOnly: boolean;
  isEditMode: boolean;
  roleLookup: Dictionary<Role>;
  updateRoleRate: (roleRate: UpdateRateForCurrency) => void;
  deleteRoleRate: (roleId: number) => void;
  setCanSubmit: (flag: boolean) => void;
}

export const RateCardsForm: FC<Props> = ({
  form,
  account,
  roleRates,
  selectedRateCard,
  selectedRateCardVersion,
  rateCardVersions,
  isCurrentRateCardVersionReadOnly,
  isEditMode,
  roleLookup,
  updateRoleRate,
  deleteRoleRate,
  setCanSubmit,
}) => {
  const inputLabel = useTranslation('settings.rate_cards.drawer.input.name');
  const inputPlaceholder = useTranslation(
    'settings.rate_cards.drawer.input.name.placeholder',
  );
  const inputRequired = useTranslation('input.required');
  const effectiveDateLabel = useTranslation(
    'settings.rate_cards.drawer.input.effective_date',
  );
  const effectDateError = useTranslation(
    'settings.rate_cards.drawer.input.effective_date.error',
  );

  const roleOptions: Option[] = useMemo(
    () =>
      Object.values(roleLookup).map(({ roleId, roleName }) => ({
        label: roleName,
        value: roleId,
      })),
    [roleLookup],
  );

  const [chosenRoles, setChosenRoles] = useState<Option[]>();

  useEffect(() => {
    setChosenRoles(roleOptions.filter(({ value }) => roleRates[value] != null));
  }, [roleOptions, roleRates]);

  const isInputDisabled = useMemo(
    () =>
      isCurrentRateCardVersionReadOnly ||
      !(
        selectedRateCard == null ||
        (selectedRateCardVersion.rateCardVersionId > 0 ? isEditMode : true)
      ),
    [
      isCurrentRateCardVersionReadOnly,
      isEditMode,
      selectedRateCard,
      selectedRateCardVersion.rateCardVersionId,
    ],
  );

  const today = useMemo(() => moment().startOf('day'), []);
  const disabledDate = useCallback(
    (date) => date.isBefore(today, 'day'),
    [today],
  );
  const timezoneLabel = useMemo(
    () =>
      selectedRateCard != null ? (
        <FormattedMessage
          id="settings.rate_cards.drawer.input.effective_date.help"
          values={{ timezone: getTimezoneLabelWithOffset(account.timeZone) }}
        />
      ) : (
        <></>
      ),
    [account.timeZone, selectedRateCard],
  );

  const deleteRow = useCallback(
    (roleId: number) => {
      setChosenRoles((prev) => prev?.filter(({ value }) => value !== roleId));
      deleteRoleRate(roleId);
    },
    [deleteRoleRate],
  );

  const isEffectiveDateValid = useCallback(
    (date: moment.Moment) =>
      rateCardVersions.find(
        ({ rateCardVersionId, effectiveDate }) =>
          rateCardVersionId !== selectedRateCardVersion.rateCardVersionId &&
          moment(effectiveDate)
            .startOf('day')
            .isSame(moment(date).startOf('day')),
      ) == null,
    [rateCardVersions, selectedRateCardVersion.rateCardVersionId],
  );

  const updateButtonStatus = useCallback(() => {
    const { rateCardName, effectiveDate } = form.getFieldsValue();

    const effectiveDateCheck =
      effectiveDate != null && isEffectiveDateValid(effectiveDate);

    setCanSubmit(
      effectiveDateCheck &&
        (selectedRateCard != null ||
          (rateCardName != null && rateCardName !== '')),
    );
  }, [isEffectiveDateValid, form, selectedRateCard, setCanSubmit]);

  const onSelectedRolesChange = useCallback(
    (selectedRoles: Option[]) => {
      selectedRoles.forEach(({ value }) => {
        updateRoleRate({
          roleId: roleLookup[value].roleId,
          roleName: roleLookup[value].roleName,
          currency: account.currency,
          billRate: 0,
        });
      });

      setChosenRoles(selectedRoles);
    },
    [account.currency, roleLookup, updateRoleRate],
  );

  useEffect(() => {
    form.setFieldsValue({
      effectiveDate: moment(selectedRateCardVersion.effectiveDate),
    });
  }, [form, selectedRateCardVersion.effectiveDate]);

  useEffect(() => {
    if (selectedRateCardVersion != null) {
      if (selectedRateCardVersion.rateCardId > 0) {
        form.validateFields().then(() => {
          updateButtonStatus();
        });
      }
      updateButtonStatus();
    }
  }, [form, selectedRateCardVersion, updateButtonStatus]);

  return (
    <Form form={form} layout="vertical" onValuesChange={updateButtonStatus}>
      <InputWrapper>
        {selectedRateCard == null && (
          <Form.Item
            label={inputLabel}
            name="rateCardName"
            rules={[
              {
                required: true,
                message: inputRequired,
              },
            ]}
          >
            <Input
              autoFocus
              placeholder={inputPlaceholder}
              onPressEnter={({ currentTarget }) => currentTarget.blur()}
            />
          </Form.Item>
        )}
        <Form.Item
          label={
            <StyledFormLabel>
              {effectiveDateLabel}
              <Tooltip
                title={
                  <FormattedMessage id="settings.rate_cards.drawer.input.effective_date.tooltip" />
                }
              >
                <Information16 />
              </Tooltip>
            </StyledFormLabel>
          }
          name="effectiveDate"
          rules={[
            () => ({
              validator(_, value) {
                const isDateAllowed = isEffectiveDateValid(value);

                if (isDateAllowed) {
                  return Promise.resolve();
                }

                return Promise.reject(new Error(effectDateError));
              },
            }),
          ]}
          help={isInputDisabled ? timezoneLabel : null}
        >
          <DatePicker
            allowClear={false}
            disabledDate={disabledDate}
            disabled={isInputDisabled}
          />
        </Form.Item>
      </InputWrapper>
      <RatesTable
        account={account}
        roleRates={roleRates}
        isInputDisabled={isInputDisabled}
        updateRoleRate={updateRoleRate}
        deleteRow={deleteRow}
      />
      {!isInputDisabled && (
        <MultiSelectPopover
          options={roleOptions}
          selected={chosenRoles}
          onChange={onSelectedRolesChange}
          placement="rightTop"
        />
      )}
    </Form>
  );
  // );
};
